import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { AlertCircle, CheckCircle2, Loader2, Star } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type {
  PublicResultReport,
  PublicResultReportItem,
  PublicResultReportSection,
} from "@eduviaos/shared/types";
import { fetchApplyResult } from "@/lib/api";
import { assessmentLabel } from "@/lib/assessment-label";
import { parsePublicError } from "@/lib/error-utils";
import { formatDateTime } from "@/lib/format-time";

/**
 * Phase F4 BE-4 + F10b — applicant-facing result report.
 *
 * Renders the redacted `PublicResultReport` (BE's `ResultReportPublic`
 * with `apply_portal_view=True`). Critical PII safety: we render only
 * the fields the BE chose to expose. We never `JSON.stringify` the raw
 * envelope into the DOM; even keys the BE redacts (e.g. proctor flags
 * for non-demoted attempts) shouldn't leak via copy-paste from devtools.
 *
 * Lazy-loaded by `<ApplyStatusSection>`: the parent only fires this
 * fetch when the user clicks "View result report" so a status-page
 * visit doesn't pay the result-fetch cost on every refresh.
 */
export function ApplyResultPanel({ token }: { token: string }) {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";

  const query = useQuery({
    queryKey: ["public-apply-result", tenantSlug, token],
    queryFn: () => fetchApplyResult(token),
    enabled: !!tenantSlug && !!token,
    // Result is immutable once graded — short stale window is enough.
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="text-muted-foreground flex items-center gap-2 py-4 text-sm"
      >
        <Loader2 className="text-primary h-4 w-4 animate-spin" />
        <span>Loading result report…</span>
      </div>
    );
  }

  if (query.error) {
    const parsed = parsePublicError(query.error);
    if (parsed.code === "ATTEMPT_NOT_GRADED") {
      return (
        <Notice tone="info">
          The result for this attempt isn't ready yet. We'll publish it once grading completes —
          check back in a little while.
        </Notice>
      );
    }
    return (
      <Notice tone="danger">
        Couldn't load the result report: {parsed.message}{" "}
        <Button type="button" variant="ghost" size="sm" onClick={() => query.refetch()}>
          Retry
        </Button>
      </Notice>
    );
  }

  const data = query.data;
  if (!data) return null;

  return <ResultBody report={data} />;
}

// ── Sub-components ────────────────────────────────────────────────────

function ResultBody({ report }: { report: PublicResultReport }) {
  const overall = report.overall_percent;
  const overallTone =
    overall === null
      ? "neutral"
      : overall >= 80
        ? "success"
        : overall >= 50
          ? "neutral"
          : "warning";

  return (
    <div className="border-border/60 bg-muted/20 grid gap-4 rounded-lg border p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-foreground text-base font-semibold tracking-tight">Result report</h3>
          <p className="text-muted-foreground text-xs">
            {assessmentLabel(report)} · version {report.template_version} · graded{" "}
            {formatDateTime(report.graded_at)}
          </p>
        </div>
        {overall !== null && <ScorePill percent={overall} tone={overallTone} />}
      </header>

      {report.outcome && <OutcomeBanner outcome={report.outcome} />}

      {report.sections.length > 0 && <SectionBreakdown sections={report.sections} />}

      {report.remediation_note && <Notice tone="info">{report.remediation_note}</Notice>}

      {report.show_correct_answers && report.items.length > 0 && <ItemList items={report.items} />}

      <footer className="text-muted-foreground text-[11px]">
        Generated {formatDateTime(report.generated_at)}. Disputes? Contact the institution directly.
      </footer>
    </div>
  );
}

function ScorePill({
  percent,
  tone,
}: {
  percent: number;
  tone: "success" | "warning" | "neutral";
}) {
  const toneClasses =
    tone === "success"
      ? "text-emerald-700 bg-emerald-500/15 border-emerald-500/40 dark:text-emerald-300"
      : tone === "warning"
        ? "text-amber-700 bg-amber-500/15 border-amber-500/40 dark:text-amber-300"
        : "text-foreground bg-card border-border";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold tabular-nums ${toneClasses}`}
    >
      <Star className="h-3.5 w-3.5" />
      {percent.toFixed(1)}%
    </span>
  );
}

function OutcomeBanner({ outcome }: { outcome: string }) {
  const isPass = outcome === "pass" || outcome === "conditional_pass";
  const tone = isPass ? "success" : outcome === "fail" ? "danger" : "info";
  const Icon = isPass ? CheckCircle2 : AlertCircle;
  const toneClasses =
    tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "danger"
        ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
        : "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300";

  const labels: Record<string, string> = {
    pass: "Passed",
    fail: "Did not pass",
    review: "Under review",
    waitlisted: "Waitlisted",
    conditional_pass: "Conditional pass",
    withdrawn: "Withdrawn",
  };

  return (
    <div className={`flex items-center gap-2 rounded-md border p-2.5 text-sm ${toneClasses}`}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="font-medium">{labels[outcome] ?? outcome.replace(/_/g, " ")}</span>
    </div>
  );
}

function SectionBreakdown({ sections }: { sections: PublicResultReportSection[] }) {
  return (
    <div className="grid gap-2">
      <h4 className="text-foreground text-xs font-semibold uppercase tracking-wide">
        Section breakdown
      </h4>
      <ul className="grid gap-2">
        {sections.map((section) => (
          <li
            key={section.key}
            className="border-border/60 bg-card flex flex-wrap items-center gap-2 rounded-md border p-2.5"
          >
            <span className="text-foreground flex-1 text-sm font-medium">{section.label}</span>
            <span className="text-muted-foreground text-xs tabular-nums">
              {section.raw_score} / {section.total_marks}
            </span>
            <span className="text-foreground text-sm font-semibold tabular-nums">
              {section.percent.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemList({ items }: { items: PublicResultReportItem[] }) {
  return (
    <details className="text-sm">
      <summary className="text-foreground cursor-pointer text-xs font-semibold uppercase tracking-wide">
        Question-by-question breakdown ({items.length} questions)
      </summary>
      <ul className="mt-2 grid gap-2">
        {items.map((item, idx) => {
          const correct = item.is_correct;
          const tone = correct === true ? "success" : correct === false ? "danger" : "neutral";
          const Icon = correct === true ? CheckCircle2 : correct === false ? AlertCircle : null;
          const toneClasses =
            tone === "success"
              ? "border-emerald-500/30 bg-emerald-500/10"
              : tone === "danger"
                ? "border-rose-500/30 bg-rose-500/10"
                : "border-border bg-card";
          return (
            <li
              key={item.question_id}
              className={`flex flex-col gap-1 rounded-md border p-2.5 text-xs ${toneClasses}`}
            >
              <div className="text-foreground flex items-center gap-1.5 text-xs">
                {Icon && <Icon className="h-3.5 w-3.5" />}
                <span className="font-medium">Q{idx + 1}</span>
                <span className="text-muted-foreground capitalize">
                  · {item.type.replace(/_/g, " ")}
                </span>
                {item.section && <span className="text-muted-foreground">· {item.section}</span>}
                <span className="ml-auto tabular-nums">
                  {item.awarded_marks ?? "—"} / {item.marks}
                </span>
              </div>
              {item.manual_grading_required && (
                <div className="text-muted-foreground text-[11px]">
                  Manually graded by the institution.
                </div>
              )}
              {item.grader_notes && (
                <div className="text-foreground/80 text-[11px]">
                  <span className="font-medium">Note:</span> {item.grader_notes}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function Notice({ tone, children }: { tone: "info" | "danger"; children: React.ReactNode }) {
  const toneClasses =
    tone === "danger"
      ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
      : "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300";
  return (
    <div className={`flex items-start gap-2 rounded-md border p-2.5 text-xs ${toneClasses}`}>
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}
